/**
 * AlignmentChip — THE shared "Aligned to <STATE> <code> +N states" chip.
 *
 * IIG Phase 4. Single presentational component used in all three placements:
 *   1. AI generation result (ResultScreen)
 *   2. Print library card (DesignCard, PrintLibrary.jsx)
 *   3. Printables card (PrintableCard.jsx)
 *
 * Purely presentational — the caller supplies the canonical alignment object
 * fetched from be_printables (`{ standardId, code, stateCode, siblingStateCount,
 * conceptId }`). Renders nothing unless the alignment carries both a code and a
 * state, so it degrades invisibly whenever alignment data is absent or the
 * alignment endpoint was unreachable.
 *
 * Copy rules (Phase-4 §4.16):
 *   • siblingStateCount == null (synthesized alignment, e.g. StatesBrowser) →
 *       always "Aligned to <STATE> <code>" (never "null states").
 *   • viewer's state matches → "Aligned to <STATE> <code>" + " +N states" when
 *       the concept is shared by more than one state.
 *   • no viewer state / mismatch → "Aligned in N state(s)".
 */

import { useTranslation } from 'react-i18next';

export default function AlignmentChip({ alignment, viewerStateCode, style }) {
  const { t } = useTranslation('home');
  if (!alignment?.code || !alignment?.stateCode) return null;

  const { code, stateCode, siblingStateCount } = alignment;
  const viewer = viewerStateCode ? String(viewerStateCode).toUpperCase() : null;
  const anchor = String(stateCode).toUpperCase();

  let label;
  if (siblingStateCount == null) {
    // Synthesized alignment (no fan-out count) — show the anchor jurisdiction plainly.
    label = t('alignmentChip.to', { jur: anchor, code });
  } else if (viewer && viewer === anchor) {
    // Shared across multiple jurisdictions → "+N more" (plural, count-aware).
    label = siblingStateCount > 1
      ? t('alignmentChip.toPlus', { jur: anchor, code, count: siblingStateCount - 1 })
      : t('alignmentChip.to', { jur: anchor, code });
  } else {
    label = t('alignmentChip.inN', { count: siblingStateCount });
  }

  return (
    <span
      title={t('alignmentChip.tooltip', { label })}
      style={{
        fontSize: '0.65rem',
        fontWeight: 600,
        color: 'rgba(16,185,129,0.95)',
        background: 'rgba(16,185,129,0.12)',
        border: '1px solid rgba(16,185,129,0.3)',
        padding: '2px 6px',
        borderRadius: 6,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {label}
    </span>
  );
}
